import { registerFragment } from '@neptune/cx/registry';
import { CountUp } from 'countup.js';
import './styles.scss';

interface CounterConfig {
    countFrom: string;
    countTo: string;
    duration: number;
    prefix: string;
    suffix: string;
    decimalPlaces: number;
    animationFollowScroll: boolean;
    percentageFromTop: number;
}

registerFragment('ay-animated-counter', (fragEl, config: CounterConfig, meta) => {
    const counterElement = fragEl.querySelector('.ay2-animated-counter__number');
    const { countFrom, countTo, duration, prefix, suffix, decimalPlaces, animationFollowScroll, percentageFromTop } = config;

    if (counterElement instanceof HTMLElement) {
        // Reduced motion: show the final value straight away. With duration 0, CountUp prints the end
        // value (prefix, suffix, decimals applied) without animating.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const demo = new CountUp(counterElement, parseFloat(countTo), {
                prefix: prefix,
                suffix: suffix,
                duration: 0,
                decimalPlaces: Number(decimalPlaces),
            });
            if (!demo.error) {
                demo.start();
            } else {
                console.error(demo.error);
            }
            return;
        }

        if(!animationFollowScroll) {
            const options = {
                autoAnimate: true,
                autoAnimateOnce: true,
                startVal: parseFloat(countFrom),
                prefix: prefix,
                suffix: suffix,
                duration: Number(duration) / 1000,
                decimalPlaces: Number(decimalPlaces),
            };
            const demo = new CountUp(counterElement, parseFloat(countTo), options);
            if (!demo.error) {
                demo.start();
            } else {
                console.error(demo.error);
            }
        } else {
            // 1. Setup the CountUp instance (but don't start it yet)
            const demo = new CountUp(counterElement, parseFloat(countTo), {
                startVal: parseFloat(countFrom),
                prefix: prefix,
                suffix: suffix,
                duration: Number(duration) / 1000,
                decimalPlaces: Number(decimalPlaces),
            });

            // 2. Define the Trigger Point (e.g., 50% from top)
            // We use rootMargin to "shrink" the hit box of the viewport to a single line
            const triggerMargin = `${100 - Number(percentageFromTop)}%`;

            const observerOptions = {
                // This margin tells the observer: "Only trigger when the element hits X% of the screen"
                // The format is: top, right, bottom, left.
                // A negative top margin of -50% means the trigger is 50% down the page.
                rootMargin: `0px 0px -${triggerMargin} 0px`,
                threshold: 0
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // 3. Start the animation!
                        if (!demo.error) {
                            demo.start();
                            // 4. Stop observing so it only animates once
                            observer.unobserve(counterElement);
                        } else {
                            console.error(demo.error);
                        }
                    }
                });
            }, observerOptions);

            observer.observe(counterElement);
        }
    }
});

if (import.meta.hot) {
    import.meta.hot.accept();
}
